<template>
  <section>
    <e-filters
      ref="filters"
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <template #after_buttons>
        <e-button
          id="installment-sale-download"
          class="ml-1 btn-sm-block"
          variant="primary"
          icon="download"
          :busy="downloading"
          :text="$t('Baixar Relatório')"
          @click="downloadReport"
        />
        <!-- Âncora do download, acionada por código. -->
        <a
          ref="downloadLink"
          style="display: none"
        />
      </template>

      <div>
        <b-row>
          <b-col md="4">
            <e-store-combo
              id="installment-sale-store_id"
              v-model="filters.storeId"
              :required="false"
            />
          </b-col>
          <b-col md="3">
            <FormulateInput
              id="installment-sale-period"
              v-model="filters.rangeDate"
              type="date-range-picker"
              :label="$t('Período da venda')"
            />
          </b-col>
          <b-col md="2">
            <FormulateInput
              id="installment-sale-channel"
              v-model="filters.paymentChannel"
              type="vue-select"
              :label="$t('Canal')"
              :placeholder="$t('Todos')"
              :options="paymentChannelOptions"
            />
          </b-col>

          <b-col md="3">
            <FormulateInput
              id="installment-sale-card_brand"
              v-model="filters.cardBrand"
              type="text"
              :label="$t('Bandeira')"
            />
          </b-col>
        </b-row>
      </div>
    </e-filters>

    <!--
      Os totais vêm somados no banco sobre o filtro inteiro, e não sobre a página: um resumo que
      mudasse ao virar a página não resumiria nada.
    -->
    <b-card-actions
      v-if="summary"
      no-actions
      :title="$t('Resumo do período')"
    >
      <b-row>
        <b-col
          v-for="card in summaryCards"
          :key="card.label"
          md="3"
          class="mb-1"
        >
          <div class="summary-card">
            <p class="mb-0 text-muted">
              {{ card.label }}
            </p>
            <p
              class="h4 mb-0"
              :class="card.variant"
            >
              {{ card.value }}
            </p>
          </div>
        </b-col>
      </b-row>
    </b-card-actions>

    <b-card-actions
      action-refresh
      :show-loading="fetching"
      @refresh="getData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          @change="pageSizeChange"
        />
      </div>

      <b-table
        id="installment-sale-table"
        show-empty
        responsive
        striped
        small
        class="bordered"
        :empty-text="
          queryExecuted
            ? $t('Nenhuma venda parcelada encontrada para o filtro informado.')
            : $t('Informe os filtros e clique em Pesquisar.')
        "
        :fields="fields"
        :items="results"
      >
        <template #cell(divergence)="row">
          <b-badge
            v-if="hasDivergence(row.item)"
            variant="light-danger"
          >
            {{ row.item.policyDivergenceDetail }}
          </b-badge>
          <span v-else>-</span>
        </template>
      </b-table>

      <b-row>
        <b-col cols="6">
          <e-pagination-summary
            :current-page="paging.currentPage"
            :per-page="paging.pageSize"
            :total="paging.rowCount"
            :total-on-page="paging.rowsInCurrentPage"
          />
        </b-col>
        <b-col cols="6">
          <b-pagination
            v-model="paging.currentPage"
            align="right"
            :total-rows="paging.rowCount"
            :per-page="paging.pageSize"
            aria-controls="installment-sale-table"
            @change="pageChange"
          />
        </b-col>
      </b-row>
    </b-card-actions>
  </section>
</template>

<script>
import { BRow, BCol, BTable, BPagination, BBadge } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { mapActions, mapState } from 'vuex'
import moment from 'moment'
import { cardDomain } from '@/mixins'
import EFilters from '@/views/components/EFilters.vue'
import EButton from '@/views/components/EButton.vue'
import EStoreCombo from '@/views/components/inputs/EStoreCombo.vue'
import EPaginationSummary from '@/views/components/pagination/EPaginationSummary.vue'
import EPageSizeSelector from '@/views/components/pagination/EPageSizeSelector.vue'

const STORE = 'pages/reports/sales/installmentSale'

export default {
  components: {
    BRow,
    BCol,
    BTable,
    BPagination,
    BBadge,
    BCardActions,
    EFilters,
    EButton,
    EStoreCombo,
    EPaginationSummary,
    EPageSizeSelector,
  },

  mixins: [cardDomain],

  data() {
    return {
      fetching: false,
      downloading: false,
      queryExecuted: false,
    }
  },

  computed: {
    ...mapState(STORE, ['results', 'summary', 'paging', 'filters', 'reportData']),

    paymentChannelOptions() {
      return [
        { value: 'Tef', label: this.$t('TEF') },
        { value: 'Pos', label: this.$t('POS') },
        { value: 'SmartPos', label: this.$t('SmartPOS') },
      ]
    },

    installmentModeOptions() {
      return [
        { value: 'Cash', label: this.$t('À vista') },
        { value: 'Store', label: this.$t('Parcelado sem juros') },
        { value: 'Issuer', label: this.$t('Parcelado com juros') },
      ]
    },

    /**
     * Cartões do resumo.
     *
     * O custo efetivo aparece em pontos percentuais porque é o número comparável entre planos de
     * parcelas diferentes: o custo em reais cresce com o prazo e sozinho não diz se está caro.
     */
    summaryCards() {
      const currency = value => this.$options.filters.currency(value, null, 2)
      const percent = value => `${Number(value || 0).toFixed(2)}%`

      return [
        { label: this.$t('Pagamentos parcelados'), value: this.summary.paymentCount },
        { label: this.$t('Média de parcelas'), value: this.summary.averageInstallments },
        { label: this.$t('Valor sem acréscimo'), value: currency(this.summary.totalWithoutSurcharge) },
        { label: this.$t('Acréscimo cobrado'), value: currency(this.summary.totalSurcharge) },
        { label: this.$t('Total cobrado'), value: currency(this.summary.totalCharged) },
        { label: this.$t('Custo previsto'), value: currency(this.summary.totalEstimatedCost) },
        { label: this.$t('Custo efetivo'), value: percent(this.summary.effectiveCostPercent) },
        {
          label: this.$t('Com divergência'),
          value: this.summary.divergentCount,
          variant: this.summary.divergentCount > 0 ? 'text-danger' : '',
        },
      ]
    },

    fields() {
      const currency = value => this.$options.filters.currency(value, null, 2)
      const percent = value => (value == null ? '-' : `${Number(value).toFixed(2)}%`)

      return [
        {
          label: this.$t('Venda'),
          key: 'saleId',
          class: 'text-center',
          thStyle: { width: '90px' },
        },
        {
          label: this.$t('Data'),
          key: 'saleDate',
          class: 'text-center',
          thStyle: { width: '110px' },
          formatter: value => this.$options.filters.date(value),
        },
        { label: this.$t('Loja'), key: 'storeName' },
        {
          label: this.$t('Bandeira'),
          key: 'cardBrand',
          class: 'text-center',
          formatter: value => value || '-',
        },
        {
          label: this.$t('Canal'),
          key: 'paymentChannel',
          class: 'text-center',
          formatter: value => value || '-',
        },
        {
          label: this.$t('Parcelas'),
          key: 'installmentCount',
          class: 'text-center',
          thStyle: { width: '90px' },
          formatter: value => `${value}x`,
        },
        {
          label: this.$t('Sem acréscimo'),
          key: 'valueWithoutSurcharge',
          class: 'text-right',
          formatter: currency,
        },
        {
          label: this.$t('Acréscimo'),
          key: 'surchargeValue',
          class: 'text-right',
          formatter: currency,
        },
        {
          label: this.$t('Cobrado'),
          key: 'chargedValue',
          class: 'text-right',
          formatter: currency,
        },
        {
          label: this.$t('Custo previsto'),
          key: 'estimatedFinancialCost',
          class: 'text-right',
          formatter: currency,
        },
        {
          label: this.$t('Custo efetivo'),
          key: 'effectiveCostPercent',
          class: 'text-right',
          thStyle: { width: '110px' },
          formatter: percent,
        },
        {
          label: this.$t('Margem'),
          key: 'coverageMarginPoints',
          class: 'text-right',
          thStyle: { width: '100px' },
          // Em pontos percentuais: negativa é o que a loja absorve do custo.
          formatter: value => (value == null ? '-' : `${Number(value).toFixed(2)} p.p.`),
        },
      ]
    },
  },

  mounted() {
    this.stCleanState()
  },

  methods: {
    ...mapActions(STORE, {
      stFetchReport: 'fetchReport',
      stGenerateReport: 'generateReport',
      stResetFilters: 'resetFilters',
      stCleanState: 'cleanState',
      stSetCurrentPage: 'setCurrentPage',
      stSetPageSize: 'setPageSize',
    }),

    hasDivergence(row) {
      return row.policyDivergence && row.policyDivergence !== 'None'
    },

    async getData() {
      try {
        this.fetching = true
        await this.stFetchReport()
        this.queryExecuted = true
      } catch (error) {
        this.showError({ error })
      } finally {
        this.fetching = false
      }
    },

    filterData() {
      this.stSetCurrentPage(1)
      this.getData()
    },

    pageChange(currentPage) {
      this.stSetCurrentPage(currentPage)
      this.getData()
    },

    pageSizeChange(pageSize) {
      this.stSetPageSize(pageSize)
      this.getData()
    },

    resetFiltersLocal() {
      this.stResetFilters()
      this.queryExecuted = false
    },

    async downloadReport() {
      try {
        this.downloading = true
        await this.stGenerateReport()

        const { downloadLink } = this.$refs

        if (!this.reportData) {
          this.showError({ message: this.$t('Não foi possível gerar o relatório') })
          return
        }

        downloadLink.href = this.reportData
        downloadLink.download = `Vendas_Parceladas_${moment().format('YYYY-MM-DD_HH-mm')}.xlsx`
        downloadLink.click()
      } catch (error) {
        this.showError({ error })
      } finally {
        this.downloading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.summary-card {
  border-left: 3px solid var(--primary);
  padding-left: 0.75rem;
}
</style>
